import React, { useEffect } from 'react';
import { Form, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { T, H_TITLE, SECTION_LABEL } from './declarationTheme';
import { Field, Grid2, Input, Select, inputProps, selectProps } from './fields';
import { filtreSansAccents } from '../../../utils/rechercheTexte';

/* ─────────────────────────────────────────────────────────────
   Étape 1 - Contexte du sinistre
   Détermine :
     - typeSinistre : SURVENU_TOGO (véhicule étranger au Togo)
                      SURVENU_ETRANGER (véhicule togolais à l'étranger)
     - paysGestionnaire / paysEmetteur (un des deux est toujours le Togo)
     - compagnie homologue (bureau national partenaire)
   ───────────────────────────────────────────────────────────── */

/* Détection Togo tolérante aux variations ISO2/ISO3, casse, libellé. */
const isTogoPays = (p) => {
  if (!p) return false;
  const iso = String(p.codeIso || '').trim().toUpperCase();
  const bcb = String(p.codeCarteBrune || '').trim().toUpperCase();
  const lib = String(p.libelle || '').trim().toLowerCase();
  return iso === 'TGO' || iso === 'TG' || bcb === 'TG' || bcb === 'TGO' || lib === 'togo';
};

export default function StepContexte({ form, pays = [], organismes = [] }) {
  const { t } = useTranslation('declaration');
  const typeSinistre = Form.useWatch('typeSinistre', form);
  const paysEmetteurId = Form.useWatch('paysEmetteurTrackingId', form);
  const paysGestionnaireId = Form.useWatch('paysGestionnaireTrackingId', form);

  const togo = pays.find(isTogoPays);
  const paysEtrangers = pays.filter(p => !isTogoPays(p));
  const bureauxHomologues = organismes.filter(o => o.typeOrganisme === 'BUREAU_HOMOLOGUE');

  /* Verrouillage automatique Togo selon le type de sinistre */
  useEffect(() => {
    if (!togo || !typeSinistre) return;
    if (typeSinistre === 'SURVENU_TOGO') {
      form.setFieldValue('paysGestionnaireTrackingId', togo.paysTrackingId);
    } else if (typeSinistre === 'SURVENU_ETRANGER') {
      form.setFieldValue('paysEmetteurTrackingId', togo.paysTrackingId);
    }
  }, [typeSinistre, togo, form]);

  /* Auto-remplissage du bureau homologue en fonction du pays étranger choisi */
  useEffect(() => {
    const paysEtrangerId = typeSinistre === 'SURVENU_TOGO' ? paysEmetteurId : paysGestionnaireId;
    if (!paysEtrangerId) return;
    const paysEtranger = pays.find(p => p.paysTrackingId === paysEtrangerId);
    if (!paysEtranger) return;
    const homologue = bureauxHomologues.find(o =>
      (paysEtranger.codeIso && o.codePays === paysEtranger.codeIso) ||
      (paysEtranger.codeCarteBrune && o.codePaysBCB === paysEtranger.codeCarteBrune)
    );
    if (homologue) {
      form.setFieldValue('organismeHomologueTrackingId', homologue.organismeTrackingId);
    } else {
      form.setFieldValue('organismeHomologueTrackingId', undefined);
    }
  }, [paysEmetteurId, paysGestionnaireId, typeSinistre, pays, bureauxHomologues, form]);

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={H_TITLE}>{t('contexte.titre')}</h1>

      <div style={{ marginTop: 40 }}>
        <div style={SECTION_LABEL}>{t('contexte.typeSinistre')}</div>

        <Form.Item
          name="typeSinistre"
          rules={[{ required: true, message: t('contexte.typeSinistreRequis') }]}
          style={{ marginBottom: 24 }}
        >
          <TypeSinistreRadios />
        </Form.Item>
      </div>

      {typeSinistre && (
        <>
          <div style={{ marginTop: 24 }}>
            <div style={SECTION_LABEL}>{t('contexte.bureauxPays')}</div>

            <Grid2>
              {typeSinistre === 'SURVENU_TOGO' ? (
                <Field
                  name="paysEmetteurTrackingId"
                  label={t('contexte.paysEmetteur')}
                  rules={[
                    { required: true, message: t('commun.obligatoire') },
                    {
                      validator: (_, v) => {
                        const gest = form.getFieldValue('paysGestionnaireTrackingId');
                        return v && v === gest
                          ? Promise.reject(new Error(t('contexte.emetteurDifferent')))
                          : Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    {...selectProps}
                    showSearch
                    filterOption={filtreSansAccents}
                    placeholder={t('contexte.selectionnerPaysCedeao')}
                    optionFilterProp="label"
                    options={paysEtrangers.map(p => ({
                      label: `${p.libelle} (${p.codeCarteBrune || p.codeIso})`,
                      value: p.paysTrackingId,
                    }))}
                  />
                </Field>
              ) : (
                <Field
                  name="paysGestionnaireTrackingId"
                  label={t('contexte.paysAccident')}
                  rules={[
                    { required: true, message: t('commun.obligatoire') },
                    {
                      validator: (_, v) => {
                        const emet = form.getFieldValue('paysEmetteurTrackingId');
                        return v && v === emet
                          ? Promise.reject(new Error(t('contexte.gestionnaireDifferent')))
                          : Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    {...selectProps}
                    showSearch
                    filterOption={filtreSansAccents}
                    placeholder={t('contexte.selectionnerPaysCedeao')}
                    optionFilterProp="label"
                    options={paysEtrangers.map(p => ({
                      label: `${p.libelle} (${p.codeCarteBrune || p.codeIso})`,
                      value: p.paysTrackingId,
                    }))}
                  />
                </Field>
              )}

              <Field
                name="organismeHomologueTrackingId"
                label={t('contexte.bureauHomologue')}
              >
                <Select
                  {...selectProps}
                  showSearch
                  filterOption={filtreSansAccents}
                  allowClear
                  disabled={
                    !!(typeSinistre === 'SURVENU_TOGO' ? paysEmetteurId : paysGestionnaireId)
                  }
                  placeholder={t('contexte.selectionnerSiConnu')}
                  optionFilterProp="label"
                  options={organismes
                    .filter(o => o.typeOrganisme === 'BUREAU_HOMOLOGUE')
                    .map(o => ({ label: o.raisonSociale, value: o.organismeTrackingId }))}
                />
              </Field>
            </Grid2>
          </div>

          <div style={{ marginTop: 24 }}>
            <div style={SECTION_LABEL}>{t('contexte.referencesExternes')}</div>

            <Grid2>
              <Field
                name="numeroSinistreEcarteBrune"
                label={t('contexte.numeroECarteBrune')}
              >
                <Input {...inputProps} placeholder={t('contexte.referenceECarteBrune')} maxLength={50} />
              </Field>

              <Field
                name="numeroSinistreHomologue"
                label={t('contexte.numeroSinistreHomologue')}
              >
                <Input {...inputProps} placeholder={t('contexte.numeroSinistreHomologueExemple')} maxLength={50} />
              </Field>

            </Grid2>
          </div>

          <Alert
            type="warning"
            showIcon
            banner
            style={{
              marginTop: 28,
              borderRadius: T.r.md,
              fontSize: 14,
              fontWeight: 600,
              padding: '14px 18px',
              border: '2px solid #f59e0b',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
            }}
            message={
              typeSinistre === 'SURVENU_TOGO'
                ? t('contexte.alerteEt')
                : t('contexte.alerteTe')
            }
          />
        </>
      )}
    </div>
  );
}

/* ─── Sélecteur type sinistre : 2 cartes côte à côte ─── */
function TypeSinistreRadios({ value, onChange }) {
  const { t } = useTranslation('declaration');
  const options = [
    {
      value: 'SURVENU_TOGO',
      code: 'ET',
      titre: t('contexte.types.survenuTogo.titre'),
      desc: t('contexte.types.survenuTogo.description'),
    },
    {
      value: 'SURVENU_ETRANGER',
      code: 'TE',
      titre: t('contexte.types.survenuEtranger.titre'),
      desc: t('contexte.types.survenuEtranger.description'),
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
      {options.map(opt => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange?.(opt.value)}
            style={{
              textAlign: 'left',
              padding: '18px 18px 16px',
              border: `1.5px solid ${selected ? T.brand : T.line}`,
              borderRadius: T.r.md,
              background: selected ? T.brandSoft : T.white,
              cursor: 'pointer',
              transition: 'all 160ms ease',
              position: 'relative',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: 11,
                fontFamily: T.fontMono,
                fontWeight: 600,
                color: selected ? T.brand : T.inkMuted,
                letterSpacing: '0.08em',
                marginBottom: 8,
              }}
            >
              {opt.code}
            </span>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: T.ink,
                marginBottom: 4,
                letterSpacing: '-0.005em',
              }}
            >
              {opt.titre}
            </div>
            <div style={{ fontSize: 12.5, color: T.inkMuted, lineHeight: 1.5 }}>
              {opt.desc}
            </div>
          </button>
        );
      })}
    </div>
  );
}
